import { useState } from "react";
import { DOCUMENT_TYPES } from "@miplatform/constants";
import { useBoolean } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { IAttempt, IDocument, IStep } from "@miplatform/types";
import { externalReferenceId } from "@miplatform/utils";
import { useDocumentsViewModel } from "@miplatform/view-models";

export const useWorkOrderAttemptDocumentStepViewModel = ({
  attempt,
  step,
  onRefresh,
}: {
  attempt: IAttempt | null;
  step: IStep;
  onRefresh: () => void;
}) => {
  const { loading, uploadDocument, removeDocument, downloadFile, printFile } =
    useDocumentsViewModel();
  const [selectedDocument, setSelectedDocument] = useState<IDocument | null>(
    null
  );
  const [deleteFile, setDeleteFile] = useState<IDocument | null>(null);
  const [isDeleteModalOpen, handleDeleteModalOpen, handleDeleteModalClose] =
    useBoolean();

  const setDeleteDocument = (document: IDocument) => {
    setDeleteFile(document);
    handleDeleteModalOpen();
  };

  const handleDocumentSelect = (document: IDocument | null) => {
    setSelectedDocument(document);
  };

  const handleResetSelectedDocument = () => {
    setSelectedDocument(null);
  };

  const uploadFiles = async (files: any[]) => {
    if (!files || files?.length === 0 || loading) return;

    const invalidFile = files.find((file) => file.error);

    if (invalidFile) {
      toastService.error({
        text: "File Upload Failed",
        subtext: invalidFile.error || "Please check your files and try again.",
      });
      return;
    }

    const uploadPromises = files.map((file) =>
      uploadDocument({
        file,
        documentType: DOCUMENT_TYPES.UNKNOWN,
        externalReferenceId: externalReferenceId.getStepResultRefId({
          orderId: attempt?.orderId || "",
          assignmentId: attempt?.assignment?.id || "",
          attemptId: attempt?.id || "",
          stepId: step.id,
          stepResultId: step.stepResult?.id || "",
        }),
      })
    );
    const results = await Promise.all(uploadPromises);

    if (results.some((res) => res === null)) {
      toastService.error({
        text: "Upload Document Failed.",
      });
    } else {
      toastService.success({
        text: "Uploaded Document Successfully.",
      });
    }

    onRefresh();
  };

  const deleteDocument = async () => {
    const res = await removeDocument({
      id: deleteFile?.id || "",
      modifiedOn: deleteFile?.modifiedOn || "",
    });

    if (res) {
      toastService.success({
        text: "Deleted Document Successfully.",
      });
      onRefresh();
      handleDeleteModalClose();
    }
  };

  return {
    loading,
    isDeleteModalOpen,
    deleteFile,
    selectedDocument,
    downloadFile,
    printFile,
    uploadFiles,
    deleteDocument,
    setDeleteDocument,
    handleDeleteModalClose,
    handleDocumentSelect,
    handleResetSelectedDocument,
  };
};
